import { Component, OnInit, Input, ElementRef, ViewChild, Output, EventEmitter, OnChanges, ChangeDetectorRef } from '@angular/core';
import { PhotoEditorService } from '../photo-editor/photo-editor.service';
import { tap } from 'rxjs/internal/operators/tap';
import { PhotoEditorToolsZoomService } from '../photo-editor-tools/photo-editor-tools-zoom/photo-editor-tools-zoom.service';
import { PhotoEditorCanvasService } from './photo-editor-canvas.service';
import { PhotoEditorToolsTextSizeService } from '../photo-editor-tools/photo-editor-tools-text-size/photo-editor-tools-text-size.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PhotoEditorToolsMoveService } from '../photo-editor-tools/photo-editor-tools-move/photo-editor-tools-move.service';
import { PhotoEditorToolsFontSelectService } from '../photo-editor-tools/photo-editor-tools-font-select/photo-editor-tools-font-select.service';
import { empty } from 'rxjs';
import { catchError } from 'rxjs/internal/operators/catchError';

enum CanvasMode {
  Create = 'CREATE',
  Edit = 'EDIT'
}

@Component({
  selector: 'app-photo-editor-canvas',
  templateUrl: './photo-editor-canvas.component.html',
  styleUrls: ['./photo-editor-canvas.component.scss']
})
export class PhotoEditorCanvasComponent implements OnInit, OnChanges {

  @Input() source;
  cleanSource;
  @Output() result = new EventEmitter<any>();
  @ViewChild('canvas', { static: false }) canvasRef: ElementRef;
  currentZoomScale = 1;
  currentFontSize = 0.75;
  currentTool;
  currentFontColor;
  currentFont;
  selectedElement;
  canvasMode: CanvasMode;

  constructor(private photoEditorService: PhotoEditorService,
              private photoEditorCanvasService: PhotoEditorCanvasService,
              private photoEditorToolsMoveService: PhotoEditorToolsMoveService,
              private photoEditorToolsTextSizeService: PhotoEditorToolsTextSizeService,
              private photoEditorToolsZoomService: PhotoEditorToolsZoomService,
              private photoEditorToolsFontSelectService: PhotoEditorToolsFontSelectService,
              private cd: ChangeDetectorRef,
              private domSanitizer: DomSanitizer) { }

  ngOnInit() {
    this._listenAddItem();
    this._listenSelectedTool();
    this._listenZoomPlus();
    this._listenZoomMinus();
    this._listenFontSizePlus();
    this._listenFontSizeMinus();
    this._listenSave();
    this._listenSelectedElement();
    this._listenDeleteItem();
    this._listenSelectFontColor();
    this._listenMoveLeftElement();
    this._listenMoveRightElement();
    this._listenMoveUpElement();
    this._listenMoveDownElement();
    this._listenSelectFont();
  }

  ngOnChanges() {
    if (this.source) {
      switch (this.source.type) {
        case 'IMAGE':
          this.cleanSource = this.source;
          this.canvasMode = CanvasMode.Create;
          break;
        case 'WEBSCRIPT':
          this.cleanSource = this._generateInputHTML(this.source);
          this.canvasMode = CanvasMode.Edit;
          this.cd.detectChanges();
          this._addLocalElementsJavascript(this._getCanvasHTML());
          break;
        case 'WEB_SCRIPT':
          this.cleanSource = this._generateInputHTML(this.source);
          this.canvasMode = CanvasMode.Edit;
          this.cd.detectChanges();
          this._addLocalElementsJavascript(this._getCanvasHTML());
          break;
      }
    }
  }

  public click(e) {
    if (this.currentTool) {
      this.photoEditorCanvasService.click(this.currentTool, this._getCanvasHTML(), e, { color: this.currentFontColor });
    } else {
      switch (e['target']['tagName']) {
        case 'TEXTAREA':
          this.photoEditorCanvasService.selectElement(e['target']);
          this.photoEditorToolsTextSizeService.showFontSizeOptions();
          this.photoEditorToolsFontSelectService.showFontSelect(e['target']);
          this.photoEditorToolsMoveService.showMoveOptions();
          break;
          default:
            this.photoEditorCanvasService.selectElement(null);
            this.photoEditorToolsTextSizeService.hideFontSizeOptions();
            this.photoEditorToolsFontSelectService.hideFontSelect();
            this.photoEditorToolsMoveService.hideMoveOptions();
      }
    }
  }

  private _getCanvasHTML() {
    return <HTMLElement>this.canvasRef.nativeElement.children[0];
  }

  /***********************************************************************************************************/
  /********************************************  Html to Canvas  *********************************************/
  /***********************************************************************************************************/

  private _generateInputHTML(element) {
    let cleanHtml = this._cleanWebscriptElement(element.url);
    cleanHtml = cleanHtml.replace(new RegExp('<div.+?>', 'g'), '');
    cleanHtml = cleanHtml.replace(new RegExp('<\/div>', 'g'), '');
    element.url = this.domSanitizer.bypassSecurityTrustHtml(cleanHtml);
    return element;
  }

  private _cleanWebscriptElement(html) {
    let cleanHtml = '';
    cleanHtml = html.replace(new RegExp('<html>.+?<body>', 'g'), '');
    cleanHtml = cleanHtml.replace(new RegExp('<\/body>+?<\/html>', 'g'), '');
    cleanHtml = cleanHtml.replace(new RegExp('<script>.+?<\/script>', 'g'), '');
    cleanHtml = cleanHtml.replace(new RegExp('<style>.+?<\/style>', 'g'), '');
    cleanHtml = cleanHtml.replace(new RegExp('<link.+?rel="stylesheet">', 'g'), '');
    cleanHtml = cleanHtml.replace(new RegExp('<!--[^>]*-->', 'g'), '');
    return cleanHtml;
  }

  private _addLocalElementsJavascript(canvas) {
    const textareas = canvas.getElementsByTagName('textarea');
    for (let i = 0; i < textareas.length; i++) {
      textareas[i].ondragstart = function(e) {
        (textareas[i] as HTMLElement).setAttribute('data-dragstart-x', e['offsetX'].toString());
        (textareas[i] as HTMLElement).setAttribute('data-dragstart-y', e['offsetY'].toString());
      };
      textareas[i].ondragend = function(e) {
        textareas[i].style.left = ( (e['target']['offsetLeft'] + e['offsetX'] - parseInt(e['target']['dataset']['dragstartX'], 10)) / canvas.clientWidth * 100 ) + '%';
        textareas[i].style.top = ( (e['target']['offsetTop'] + e['offsetY'] - parseInt(e['target']['dataset']['dragstartY'], 10)) / canvas.clientHeight * 100 ) + '%';
      };
      textareas[i].onmousemove = function(e) {
        if ((e['offsetX'] >= -2) && (e['offsetX'] <= 2) ||
            (e['offsetX'] >= (e['target']['offsetWidth'] - 2)) && (e['offsetX'] <= e['target']['offsetWidth'] + 2) ||
            (e['offsetY'] >= -2) && (e['offsetY'] <= 2) ||
            (e['offsetY'] >= (e['target']['offsetHeight'] - 2)) && (e['offsetY'] <= e['target']['offsetHeight'] + 2)) {
          e['target']['style']['cursor'] = 'move';
        } else {
          e['target']['style']['cursor'] = 'text';
        }
      };
    }
  }

  /***********************************************************************************************************/
  /********************************************  Canvas to Html  *********************************************/
  /***********************************************************************************************************/

  private _generateOutputHTML(canvas) {
    const html_start = '<html><head>' +
    '<link href="https://fonts.googleapis.com/css2?family=PT+Serif:ital,wght@0,400;0,700;1,400;1,700&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css?family=Open+Sans:300,300i,400,400i,600,600i,700,700i,800,800i" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Fredoka+One&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Pacifico&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=PT+Mono&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Nunito&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Caveat&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Alfa+Slab+One&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Ballet&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Bangers&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Titillium+Web&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Anton&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Gloria+Hallelujah&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Prata&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=News+Cycle&display=swap" rel="stylesheet">' +
    '<link href="https://fonts.googleapis.com/css2?family=Baloo+2&display=swap" rel="stylesheet">' +
    '<style>textarea { font-family: "Open Sans", sans-serif; }</style></head><body style="margin: 0;">';
    const html_end = '</body></html>';
    return html_start + this._transformCanvasToHTML(canvas) + this._addGlobalScripts(canvas) + html_end;
  }

  private _transformCanvasToHTML(canvas) {
    const textareas = canvas.getElementsByTagName('textarea');
    for (let i = 0; i < textareas.length; i++) {
      textareas[i]['defaultValue'] = textareas[i]['value'];
      textareas[i]['style']['border'] = 'none';
      if (textareas[i]['scrollHeight'] > textareas[i]['clientHeight']) {
        textareas[i]['style']['height'] = textareas[i]['scrollHeight'] + 'px';
      }
    }
    return canvas.innerHTML;
  }

  private _addGlobalScripts(canvas) {
    const script = document.createElement('script');
    const textareas = canvas.getElementsByTagName('textarea');
    for (let i = 0; i < textareas.length; i++) {
      const scriptContent =
      'setTimeout(function() {'
      + 'document.getElementById("' + textareas[i].id + '")["style"]["width"] = (document.getElementById("canvas")["offsetWidth"] / ' + canvas.offsetWidth + ' * ' + textareas[i].offsetWidth + ') + "px";'
      + 'document.getElementById("' + textareas[i].id + '")["style"]["height"] = (document.getElementById("canvas")["offsetWidth"] / ' + canvas.offsetWidth + ' * ' + textareas[i].offsetHeight + ') + "px";'
      + 'document.getElementById("' + textareas[i].id + '")["style"]["fontSize"] = (document.getElementById("canvas")["offsetWidth"] / ' + canvas.offsetWidth + ' * ' + parseInt(textareas[i].style.fontSize.replace('px', ''), 10) + ') + "px";'
      + 'document.getElementById("' + textareas[i].id + '")["style"]["overflow"] = "visible";'
      + 'document.getElementById("' + textareas[i].id + '")["style"]["resize"] = "none";'
      + '}, 500);';
      script.innerHTML += scriptContent;
    }
    return script.outerHTML;
  }

  /***********************************************************************************************************/
  /**********************************************  LISTENERS  ************************************************/
  /***********************************************************************************************************/

  private _listenAddItem() {
    this.photoEditorCanvasService.listenAddItem().pipe(
      tap(res => this._getCanvasHTML().appendChild(res.item)),
      tap(res => res.script ? this._getCanvasHTML().appendChild(res.script) : null),
      tap(res => this.photoEditorCanvasService.selectTool(null))
    ).subscribe();
  }

  private _listenDeleteItem() {
    this.photoEditorCanvasService.listenDeleteItem().pipe(
      tap(res => document.getElementById(this.selectedElement.id).remove()),
      tap(res => this.photoEditorCanvasService.selectElement(null))
    ).subscribe();
  }

  private _listenSelectedTool() {
    this.photoEditorCanvasService.listenSelectedTool().pipe(
      tap(res => res ? this.currentTool = res : this.currentTool = null),
    ).subscribe();
  }

  private _listenSelectFontColor() {
    this.photoEditorCanvasService.listenSelectFontColor().pipe(
      tap(res => this.currentFontColor = res),
      tap(res => this.selectedElement ? document.getElementById(this.selectedElement.id).style.color = this.currentFontColor.hex : null)
    ).subscribe();
  }

  private _listenSelectedElement() {
    this.photoEditorCanvasService.listenSelectedElement().pipe(
      tap(res => {if (res) {
        this.selectedElement = res;
      } else {
        this.selectedElement = null;
        this.photoEditorToolsTextSizeService.hideFontSizeOptions();
      }}),
    ).subscribe();
  }

  private _listenZoomPlus() {
    this.photoEditorToolsZoomService.listenZoomPlus().pipe(
      tap(res => this.canvasRef.nativeElement.style.transform = 'translate(-50%, -50%) scale(' + (this.currentZoomScale < 4 ? this.currentZoomScale += 0.05 : null) + ')')
    ).subscribe();
  }

  private _listenZoomMinus() {
    this.photoEditorToolsZoomService.listenZoomMinus().pipe(
      tap(res => this.canvasRef.nativeElement.style.transform = 'translate(-50%, -50%) scale(' + (this.currentZoomScale > 0.2 ? this.currentZoomScale -= 0.05 : null) + ')')
    ).subscribe();
  }

  private _listenFontSizePlus() {
    this.photoEditorToolsTextSizeService.listenFontSizePlus().pipe(
      tap(res => this.currentFontSize < 1 ? this.currentFontSize += 0.05 : null),
      tap(res => document.getElementById(this.selectedElement.id)['style']['fontSize'] = (document.getElementById(this.selectedElement.id)['offsetHeight'] * this.currentFontSize) + 'px'),
    ).subscribe();
  }

  private _listenFontSizeMinus() {
    this.photoEditorToolsTextSizeService.listenFontSizeMinus().pipe(
      tap(res => this.currentFontSize > 0.1 ? this.currentFontSize -= 0.05 : null),
      tap(res => document.getElementById(this.selectedElement.id)['style']['fontSize'] = (document.getElementById(this.selectedElement.id)['offsetHeight'] * this.currentFontSize) + 'px')
    ).subscribe();
  }

  private _listenMoveLeftElement() {
    this.photoEditorCanvasService.listenMoveLeftElement().pipe(
      tap(res => this.selectedElement && (parseFloat(this.selectedElement.style.left) > (0.5 / window['devicePixelRatio'])) ? this.selectedElement.style.left = (parseFloat(this.selectedElement.style.left) - (0.5 / window['devicePixelRatio'])) + '%' : null),
    ).subscribe();
  }
  private _listenMoveRightElement() {
    this.photoEditorCanvasService.listenMoveRightElement().pipe(
      tap(res => this.selectedElement && (parseFloat(this.selectedElement.style.left) < (99.5 / window['devicePixelRatio'])) ? this.selectedElement.style.left = (parseFloat(this.selectedElement.style.left) + (0.5 / window['devicePixelRatio'])) + '%' : null),
    ).subscribe();
  }
  private _listenMoveUpElement() {
    this.photoEditorCanvasService.listenMoveUpElement().pipe(
      tap(res => this.selectedElement && (parseFloat(this.selectedElement.style.top) > (0.5 / window['devicePixelRatio'])) ? this.selectedElement.style.top = (parseFloat(this.selectedElement.style.top) - (0.5 / window['devicePixelRatio'])) + '%' : null),
    ).subscribe();
  }
  private _listenMoveDownElement() {
    this.photoEditorCanvasService.listenMoveDownElement().pipe(
      tap(res => this.selectedElement && (parseFloat(this.selectedElement.style.top) < (99.5 / window['devicePixelRatio'])) ? this.selectedElement.style.top = (parseFloat(this.selectedElement.style.top) + (0.5 / window['devicePixelRatio'])) + '%' : null),
    ).subscribe();
  }

  private _listenSave() {
    this.photoEditorService.listenSave().subscribe(
      (res) => {
        this.result.emit(this._generateOutputHTML(this.canvasRef.nativeElement));
      }
    );
  }

  private _listenSelectFont() {
    this.photoEditorToolsFontSelectService.listenSelectFont().pipe(
      tap(res => this.currentFont = res),
      tap(res => {
        try {
          this.selectedElement ? document.getElementById(this.selectedElement.id).style.fontFamily = this.currentFont.family : null;
        } catch (e) {
          console.dir(e);
        }
      })
    ).subscribe();
  }
}

